// Tipos principais para o sistema de festejos

export interface Theme {
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
}

export interface BaseSection {
  id: string;
  type: SectionType;
  title: string;
  order: number;
}

export interface TextoSection extends BaseSection {
  type: 'texto';
  content: string;
}

export interface ImagemSection extends BaseSection {
  type: 'imagem';
  imageUrl: string;
  description?: string;
}

export interface GaleriaImage {
  id: string;
  url: string;
  caption?: string;
}

export interface GaleriaSection extends BaseSection {
  type: 'galeria';
  description?: string;
  images: GaleriaImage[];
}

export interface VideoSection extends BaseSection {
  type: 'video';
  videoUrl: string;
  description?: string;
}

export interface LinkExternoSection extends BaseSection {
  type: 'link_externo';
  url: string;
  linkText: string;
  description?: string;
}

export interface ProgramacaoEvent {
  id: string;
  title: string;
  dateTime: string;
  location?: string;
  description?: string;
}

export interface ProgramacaoSection extends BaseSection {
  type: 'programacao';
  events: ProgramacaoEvent[];
}

export type Section = 
  | TextoSection 
  | ImagemSection 
  | GaleriaSection 
  | VideoSection 
  | LinkExternoSection 
  | ProgramacaoSection;

export type SectionType = 
  | 'texto' 
  | 'imagem' 
  | 'galeria' 
  | 'video' 
  | 'link_externo' 
  | 'programacao';

export type FestojoStatus = 'draft' | 'published' | 'archived';

export interface Festejo {
  id?: string;
  title: string;
  slug: string;
  padroeiro: string;
  description: string;
  startDate: string;
  endDate: string;
  paroquia: string;
  endereco: string;
  cidade: string;
  estado: string;
  mapLink?: string;
  contactEmail: string;
  contactPhone: string;
  coverImage?: string;
  theme: Theme;
  sections: Section[];
  status: FestojoStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface FestojoFormData extends Omit<Festejo, 'createdAt' | 'updatedAt'> {
  id?: string;
}
// Constantes
export const SECTION_TYPES: Record<SectionType, { label: string; description: string; icon: string }> = {
  texto: {
    label: 'Texto',
    description: 'Conteúdo descritivo, como história do padroeiro ou tradições',
    icon: '📝'
  },
  imagem: {
    label: 'Imagem',
    description: 'Uma imagem única com título e descrição',
    icon: '🖼️'
  },
  galeria: {
    label: 'Galeria',
    description: 'Múltiplas imagens organizadas em galeria',
    icon: '🖼️'
  },
  video: {
    label: 'Vídeo',
    description: 'Vídeo incorporado do YouTube ou Vimeo',
    icon: '🎥'
  },
  link_externo: {
    label: 'Link Externo',
    description: 'Botão para link externo, como mapas ou formulários',
    icon: '🔗'
  },
  programacao: {
    label: 'Programação',
    description: 'Lista de eventos com data, horário e descrição',
    icon: '📅'
  }
};

export const ESTADOS_BRASIL = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

export const defaultFestojoData: FestojoFormData = {
  title: '',
  slug: '',
  padroeiro: '',
  description: '',
  startDate: '',
  endDate: '',
  paroquia: '',
  endereco: '',
  cidade: '',
  estado: '',
  mapLink: '',
  contactEmail: '',
  contactPhone: '',
  coverImage: '',
  theme: {
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
    fontFamily: 'Roboto'
  },
  sections: [],
  status: 'draft'
};

// Utilitários
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

export const generateSectionId = (): string => {
  return `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const generateEventId = (): string => {
  return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

